/**
 * Publishes the height of the on-screen keyboard as the `--keyboard-inset` custom
 * property, so the layout can keep itself above it.
 *
 * `interactive-widget=resizes-content` in the viewport meta already does this properly
 * in Chrome: the layout viewport itself shrinks, so `height: 100%` and a `bottom: 0`
 * sticky bar land above the keyboard with no help at all. Safari and Firefox ignore the
 * key and shrink only the *visual* viewport — the document keeps its full height, the
 * browser scrolls it to bring the focused add box into view, and everything above the
 * add bar leaves the screen. That is the bug this exists for.
 *
 * Where the viewport key is honoured `window.innerHeight` shrinks along with the visual
 * viewport, so the inset works out at zero and this stays inert rather than subtracting
 * the keyboard's height a second time.
 */
export function trackKeyboardInset(): void {
  const viewport = window.visualViewport;
  if (!viewport) {
    return;
  }

  const update = () => {
    document.documentElement.style.setProperty('--keyboard-inset', `${measure(viewport)}px`);
  };

  // `scroll` as well as `resize`: panning the visual viewport moves its top edge, and the
  // gap at the bottom changes with it.
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
  update();
}

function measure(viewport: VisualViewport): number {
  // Pinch zoom shrinks the visual viewport too and is indistinguishable from a keyboard
  // here. Leave the layout alone rather than guess, and let the page be panned.
  if (viewport.scale > 1) {
    return 0;
  }

  const hidden = window.innerHeight - viewport.height - viewport.offsetTop;

  // Rounded because a fractional device pixel ratio produces a permanent sub-pixel
  // difference, which would otherwise read as a keyboard one pixel tall.
  return Math.max(0, Math.round(hidden));
}
